import { useState } from 'react';
import {
  Box, TextField, Snackbar, IconButton, Typography, Link,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../common/components/LocalizationProvider';
import CloseIcon from '@mui/icons-material/Close';
import LoginLayout from './LoginLayout';
import { LoadingButton } from './components';
import getLoginTokens from './loginTokens';

const useStyles = makeStyles()((theme) => {
  const P = getLoginTokens(theme.palette.mode === 'dark', theme.palette.primary.main);
  return {
    formCard: {
      animation: 'loginSlideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards',
      opacity: 0,
    },
    customInput: {
      '& .MuiOutlinedInput-root': {
        borderRadius: '10px !important',
        backgroundColor: `${P.inputBg} !important`,
        fontSize: '0.92rem',
      },
      '& .MuiOutlinedInput-root fieldset': {
        borderColor: `${P.inputBorder} !important`,
        borderWidth: '1.5px !important',
      },
      '& .MuiOutlinedInput-root:hover fieldset': {
        borderColor: `${P.inputBorderHover} !important`,
      },
      '& .MuiOutlinedInput-root.Mui-focused fieldset': {
        borderColor: `${P.primary} !important`,
        borderWidth: '1.5px !important',
        boxShadow: `0 0 0 3px ${P.focusShadow}`,
      },
      '& .MuiOutlinedInput-input': {
        padding: '0.85rem 1rem',
      },
    },
  };
});

const ChangeServerPage = () => {
  const { classes } = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const P = getLoginTokens(isDark, theme.palette.primary.main);
  const t = useTranslation();
  const navigate = useNavigate();

  const [hostname, setHostname] = useState(window.localStorage.getItem('hostname') || '');
  const [snackbar, setSnackbar] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChangeServer = async (event) => {
    event.preventDefault();
    if (!hostname) {
      setSnackbar(t('sharedHostnameRequired'));
      return;
    }
    window.localStorage.setItem('hostname', hostname);
    setSuccess(true);
    setSnackbar(t('settingsServerChanged'));
    setTimeout(() => {
      setSuccess(false);
      navigate('/login');
    }, 1000);
  };

  const labelSx = {
    display: 'block',
    fontSize: '0.82rem',
    fontWeight: 600,
    color: P.textPrimary,
    mb: 0.5,
  };

  return (
    <LoginLayout>
      <style>{`@keyframes loginSlideUp { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }`}</style>

      <Box className={classes.formCard} sx={{ width: '100%', maxWidth: 400, mx: 'auto' }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: P.textPrimary,
            mb: 1,
            textAlign: 'center',
            letterSpacing: '-0.01em',
            fontSize: '1.55rem',
          }}
        >
          {t('settingsServer')}
        </Typography>
        <Typography
          sx={{
            color: P.textSecondary,
            fontSize: '0.88rem',
            textAlign: 'center',
            mb: 4,
          }}
        >
          {t('loginChangeServerDescription')}
        </Typography>

        <Box component="form" onSubmit={handleChangeServer} noValidate>
          <Box sx={{ mb: 2 }}>
            <Typography component="label" htmlFor="change-hostname" sx={labelSx}>
              Hostname
            </Typography>
            <TextField
              id="change-hostname"
              required
              fullWidth
              placeholder="tracking.example.com"
              name="hostname"
              value={hostname}
              autoFocus
              variant="outlined"
              size="small"
              className={classes.customInput}
              onChange={(e) => setHostname(e.target.value)}
            />
          </Box>

          <LoadingButton
            onClick={handleChangeServer}
            success={success}
            disabled={!hostname}
          >
            {t('settingsChangeServer')}
          </LoadingButton>

          <Typography
            sx={{
              textAlign: 'center',
              mt: 2.5,
              fontSize: '0.85rem',
              color: P.textSecondary,
            }}
          >
            <Link
              component="button"
              type="button"
              onClick={() => navigate('/login')}
              underline="none"
              sx={{
                color: P.primary,
                fontWeight: 700,
                fontSize: '0.85rem',
                cursor: 'pointer',
                '&:hover': { color: P.primaryHover },
              }}
            >
              {t('sharedBack')}
            </Link>
          </Typography>
        </Box>
      </Box>

      <Snackbar
        open={!!snackbar}
        message={snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar('')}
        action={(
          <IconButton size="small" color="inherit" onClick={() => setSnackbar('')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      />
    </LoginLayout>
  );
};

export default ChangeServerPage;
